<template>
  <div class="export-stats-mapping-index">
    <page-content>
      <div class="row">
        <div class="col-lg-8 offset-lg-2">
          <ntm-block :title="$t('action.addNew')" :init-collapse="false">
            <div class="mb-3">
              <label class="text-muted text-uppercase font-size-sm mb-1" for="new-display-name">
                {{ $t('exportStatsMapping.displayName') }}
              </label>
              <input
                id="new-display-name"
                v-model="newMapping.displayName"
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('exportStatsMapping.displayName')">
            </div>
            <div class="mb-3">
              <label class="text-muted text-uppercase font-size-sm mb-1" for="new-default-value">
                {{ $t('exportStatsMapping.defaultValue') }}
              </label>
              <input
                id="new-default-value"
                v-model="newMapping.defaultValue"
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('exportStatsMapping.defaultValue')">
            </div>
            <div class="form-check mb-2">
              <input
                class="form-check-input"
                type="checkbox"
                id="new-visible"
                v-model="newMapping.isVisible">
              <label class="form-check-label" for="new-visible">
                {{ $t('exportStatsMapping.visible') }}
              </label>
            </div>
            <button type="button" class="btn btn-sm btn-success mb-2" @click="createMapping">
              {{ $t('action.addNew') }}
            </button>
          </ntm-block>
          <div class="items-wrapper position-relative">
            <le-loading v-if="loading"></le-loading>
            <draggable
              handle=".drag-handle"
              @change="changeOrder"
              v-bind="dragOptions"
              v-model="model"
              @start="drag = true"
              @end="drag = false">
              <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                <div class="block block-rounded mb-3" v-for="item in model" :key="item.uuid">
                  <div class="block-header block-header-default" @click="toggleCollapse(item, $event)">
                    <h3 class="block-title">
                      <i class="fa fa-bars drag-handle mr-3"></i>
                      {{ item.displayName || item.internalField }}
                    </h3>
                    <div class="block-options d-flex align-items-center">
                      <span
                        v-if="item.isInternalFieldGenerated"
                        class="badge badge-warning mr-2">
                        Generated
                      </span>
                      <span
                        class="badge mr-2"
                        :class="item.isVisible ? 'badge-success' : 'badge-secondary'">
                        {{ item.isVisible ? $t('exportStatsMapping.visible') : $t('exportStatsMapping.hidden') }}
                      </span>
                      <span
                        v-if="updateStates[item.uuid] === 'updated'"
                        class="badge badge-info mr-2">
                        {{ $t('updated') }}
                      </span>
                      <button
                        v-if="canDelete(item)"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="deleteMapping(item)">
                        <i class="fa fa-fw fa-trash"></i>
                      </button>
                    </div>
                  </div>
                  <div class="block-content bg-white pb-2" v-show="!isCollapsed(item.uuid)">
                    <div class="mb-3">
                      <div class="text-muted text-uppercase font-size-sm">
                        {{ $t('exportStatsMapping.internalField') }}
                      </div>
                      <div class="text-warning">{{ item.internalField }}</div>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted text-uppercase font-size-sm mb-1" :for="`display-name-${item.uuid}`">
                        {{ $t('exportStatsMapping.displayName') }}
                      </label>
                      <input
                        :id="`display-name-${item.uuid}`"
                        v-model="item.displayName"
                        type="text"
                        class="form-control form-control-sm"
                        :placeholder="$t('exportStatsMapping.displayName')"
                        @input="queueUpdate(item)">
                    </div>
                    <div class="mb-3">
                      <label class="text-muted text-uppercase font-size-sm mb-1" :for="`default-value-${item.uuid}`">
                        {{ $t('exportStatsMapping.defaultValue') }}
                      </label>
                      <input
                        :id="`default-value-${item.uuid}`"
                        v-model="item.defaultValue"
                        type="text"
                        class="form-control form-control-sm"
                        :placeholder="$t('exportStatsMapping.defaultValue')"
                        @input="queueUpdate(item)">
                    </div>
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="`visible-${item.uuid}`"
                        v-model="item.isVisible"
                        @change="queueUpdate(item)">
                      <label class="form-check-label" :for="`visible-${item.uuid}`">
                        {{ $t('exportStatsMapping.visible') }}
                      </label>
                    </div>
                  </div>
                </div>
              </transition-group>
            </draggable>
          </div>
        </div>
      </div>
    </page-content>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import debounce from 'lodash/debounce'
import LeLoading from '@/components/ntm/LeLoading.vue'
import basicIndexMixin from '@/mixins/basic.index.mixin'
import ExportFieldMappingService from '@/services/exportFieldMapping.service'

export default {
  components: { LeLoading, draggable },
  mixins: [basicIndexMixin],
  data () {
    return {
      model: [],
      drag: false,
      updateDebouncers: {},
      updateStates: {},
      collapseStates: {},
      newMapping: {
        displayName: '',
        defaultValue: '',
        isVisible: true
      },
      dragOptions: {
        animation: 200,
        group: 'export-field-mapping',
        disabled: false,
        ghostClass: 'ghost'
      }
    }
  },
  computed: {
    currentClient () {
      return this.$store.state.currentClient
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    changeOrder () {
      const payload = {
        mappings: this.model.map((item) => ({
          uuid: item.uuid
        }))
      }
      ExportFieldMappingService.changeOrder(payload)
    },
    fetchData () {
      ExportFieldMappingService.index().then((response) => {
        const items = response.data.map(this.normalizeItem)
        this.setData(items)
        items.forEach((item) => {
          this.$set(this.updateStates, item.uuid, null)
          if (this.collapseStates[item.uuid] === undefined) {
            this.$set(this.collapseStates, item.uuid, true)
          }
        })
      })
    },
    normalizeItem (item) {
      return {
        ...item,
        displayName: item.displayName ?? '',
        defaultValue: item.defaultValue ?? ''
      }
    },
    queueUpdate (item) {
      this.$set(this.updateStates, item.uuid, null)
      this.getUpdateDebouncer(item.uuid)()
    },
    getUpdateDebouncer (uuid) {
      if (!this.updateDebouncers[uuid]) {
        this.updateDebouncers[uuid] = debounce(() => {
          this.saveMapping(uuid)
        }, 600)
      }
      return this.updateDebouncers[uuid]
    },
    saveMapping (uuid) {
      const item = this.model.find((entry) => entry.uuid === uuid)
      if (!item) {
        return
      }
      this.$set(this.updateStates, uuid, 'saving')
      const payload = {
        displayName: item.displayName,
        defaultValue: item.defaultValue,
        isVisible: item.isVisible
      }
      ExportFieldMappingService.update(item.uuid, payload).then(() => {
        this.$set(this.updateStates, uuid, 'updated')
      }).finally(() => {
        this.$store.commit('loaded')
      })
    },
    createMapping () {
      const payload = {
        displayName: this.newMapping.displayName,
        defaultValue: this.newMapping.defaultValue,
        isVisible: this.newMapping.isVisible
      }
      ExportFieldMappingService.store(payload).then(() => {
        this.fetchData()
        this.newMapping = {
          displayName: '',
          defaultValue: '',
          isVisible: true
        }
      }).finally(() => {
        this.$store.commit('loaded')
      })
    },
    deleteMapping (item) {
      ExportFieldMappingService.destroy(item.uuid).then(() => {
        this.model = this.model.filter((entry) => entry.uuid !== item.uuid)
      }).finally(() => {
        this.$store.commit('loaded')
      })
    },
    canDelete (item) {
      return !item.internalField
    },
    isCollapsed (uuid) {
      return !!this.collapseStates[uuid]
    },
    toggleCollapse (item, event) {
      if (this.drag) {
        return
      }
      if (event && (event.target.closest('.drag-handle') || event.target.closest('.block-options'))) {
        return
      }
      this.$set(this.collapseStates, item.uuid, !this.isCollapsed(item.uuid))
    }
  }
}
</script>
